import { useState, useEffect, useMemo } from 'react';
import { Modal } from '@mui/material';
import { X } from 'phosphor-react';

import {
  getAllStates,
  SupplierJobAddressDetails,
  SupplierJobApiResponse,
  SupplierJobUpdate,
} from '@dill/dill-shared';

import { openDillToast } from '../../../utils/helpers';

import { bulkUpdateSupplierJobs } from '../../../redux/services/supplierJobsService';

import './AppNewSupplierJobModal.scss';

import { useAppDispatch, useAppSelector } from '../../../redux/store';

import { COLORS } from '../../../utils/colors';
import { AppButton } from '../AppButton/AppButton';
import { AppInputField } from '../AppInputField/AppInputField';
import { AppCheckBox } from '../AppCheckBox/AppCheckBox';
import { AppDropDown2 } from '../AppDropDown2/AppDropDown2';
import { PlusCircle } from 'react-feather';

type PartialSupplierJobUpdatePick = Pick<
  SupplierJobUpdate,
  'jobNumber' | 'owners' | 'GCs' | 'buyerId' | 'lenders' | 'apn' | 'customerType' | 'sureties'
>;
export const AppBulkEditSupplierJobModal = ({
  open,
  handleClose,
}: {
  open: boolean;
  handleClose: () => void;
}) => {
  const addressData = [
    {
      name: '',
      address: '',
      city: '',
      state: '',
      zipCode: '',
    },
  ];
  const INITIAL_STATE = {
    jobNumber: '',
    owners: addressData,
    GCs: addressData,
    lenders: addressData,
    sureties: addressData,
    apn: '',
  } as PartialSupplierJobUpdatePick;

  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);

  const [supplierJobInput, setSupplierJobInput] = useState(INITIAL_STATE);
  const [loading, setLoading] = useState(false);
  const [checkedFields, setCheckedFields] = useState<string[]>([]);
  const [formAddressState, setAddressFormState] = useState<{
    GCs: SupplierJobAddressDetails[];
    owners: SupplierJobAddressDetails[];
    lenders?: SupplierJobAddressDetails[];
    sureties?: SupplierJobAddressDetails[];
  }>({
    GCs: [],
    owners: [],
  });
  const [ownerAndGCSame, setOwnerAndGCSame] = useState(false);
  const [formAddressError, setAddressError] = useState<{
    GCs: string;
    owners: string;
    lenders: string;
    sureties: string;
  }>({
    GCs: '',
    owners: '',
    lenders: '',
    sureties: '',
  });
  const [showErrorAbove, setShowErrorAbove] = useState(false);
  const [errors, setErrors] = useState<{ apn: string; jobNumber: string }>({
    apn: '',
    jobNumber: '',
  });

  const { selectedSupplierJobs } = useAppSelector((state) => state.supplierJobs);

  const allStates = useMemo(() => {
    return getAllStates();
  }, []);

  useEffect(() => {
    if (open) {
      setCheckedFields([]);
      setSupplierJobInput({
        jobNumber: '',
        owners: addressData,
        GCs: addressData,
        lenders: addressData,
        sureties: addressData,
        apn: '',
      });
      setAddressFormState({
        GCs: addressData,
        owners: addressData,
        lenders: addressData,
        sureties: addressData,
      });
      setAddressError({
        GCs: '',
        owners: '',
        lenders: '',
        sureties: '',
      });
      setLoading(false);
      setShowErrorAbove(false);
      setErrors({
        apn: '',
        jobNumber: '',
      });
    }
  }, [open]);

  const handleCloseModal = () => {
    handleClose();
    setSupplierJobInput(INITIAL_STATE);
    setCheckedFields([]);
    setLoading(false);
    setAddressFormState({
      GCs: addressData,
      owners: addressData,
      lenders: addressData,
      sureties: addressData,
    });
    setAddressError({
      GCs: '',
      owners: '',
      lenders: '',
      sureties: '',
    });
    setShowErrorAbove(false);
    setErrors({
      apn: '',
      jobNumber: '',
    });
  };

  const validateAddressInputs = () => {
    const newErrors = {} as {
      GCs: string;
      owners: string;
      lenders: string;
      sureties: string;
    };

    let isValid = true;

    if (formAddressState.GCs.length > 0 && checkedFields.includes('gcs')) {
      formAddressState.GCs.some((gC) => {
        if (gC.name?.trim() === '') {
          newErrors.GCs = 'Required.';
          isValid = false;
        }
        if (gC.address?.trim() === '') {
          newErrors.GCs = 'Required.';
          isValid = false;
        }
        if (gC.city?.trim() === '') {
          newErrors.GCs = 'Required.';
          isValid = false;
        }
        if (gC.state?.trim() === '') {
          newErrors.GCs = 'Required.';
          isValid = false;
        }
        if (gC.zipCode?.trim() === '') {
          newErrors.GCs = 'Required.';
          isValid = false;
        }
      });
    }

    if (formAddressState.owners.length > 0 && checkedFields.includes('owners')) {
      formAddressState.owners.some((owner) => {
        if (owner.name?.trim() === '') {
          newErrors.owners = 'Required.';
          isValid = false;
        }
        if (owner.address?.trim() === '') {
          newErrors.owners = 'Required.';
          isValid = false;
        }
        if (owner.city?.trim() === '') {
          newErrors.owners = 'Required.';
          isValid = false;
        }
        if (owner.state?.trim() === '') {
          newErrors.owners = 'Required.';
          isValid = false;
        }
        if (owner.zipCode?.trim() === '') {
          newErrors.owners = 'Required.';
          isValid = false;
        }
      });
    }
    if (
      formAddressState.lenders &&
      formAddressState.lenders.length > 0 &&
      checkedFields.includes('lenders')
    ) {
      formAddressState.lenders.some((lender) => {
        if (lender.name?.trim() === '') {
          newErrors.lenders = 'Required.';
          isValid = false;
        }
        if (lender.address?.trim() === '') {
          newErrors.lenders = 'Required.';
          isValid = false;
        }
        if (lender.city?.trim() === '') {
          newErrors.lenders = 'Required.';
          isValid = false;
        }
        if (lender.state?.trim() === '') {
          newErrors.lenders = 'Required.';
          isValid = false;
        }
        if (lender.zipCode?.trim() === '') {
          newErrors.lenders = 'Required.';
          isValid = false;
        }
      });
    }

    if (
      formAddressState.sureties &&
      formAddressState.sureties.length > 0 &&
      checkedFields.includes('sureties')
    ) {
      formAddressState.sureties.some((surety) => {
        if (surety.name?.trim() === '') {
          newErrors.sureties = 'Required.';
          isValid = false;
        }
        if (surety.address?.trim() === '') {
          newErrors.sureties = 'Required.';
          isValid = false;
        }
        if (surety.city?.trim() === '') {
          newErrors.sureties = 'Required.';
          isValid = false;
        }
        if (surety.state?.trim() === '') {
          newErrors.sureties = 'Required.';
          isValid = false;
        }
        if (surety.zipCode?.trim() === '') {
          newErrors.sureties = 'Required.';
          isValid = false;
        }
        if (surety.bondNumber?.trim() === '') {
          newErrors.sureties = 'Required.';
          isValid = false;
        }
      });
    }

    setAddressError(newErrors);
    return isValid;
  };

  const toggleSelectField = (field: string) => {
    setErrors({
      jobNumber: '',
      apn: '',
    });
    setAddressError({
      GCs: '',
      owners: '',
      lenders: '',
      sureties: '',
    });
    setCheckedFields((prevFields) =>
      prevFields.includes(field)
        ? prevFields.filter((item) => item !== field)
        : [...prevFields, field]
    );
  };

  const validateInputsVerify = () => {
    const newErrors = {
      jobNumber: '',
      apn: '',
    } as {
      jobNumber: string;
      apn: string;
    };

    let isValid = true;
    if (supplierJobInput.jobNumber?.trim() === '' && checkedFields.includes('jobNumber')) {
      newErrors.jobNumber = 'Job number is required.';
      isValid = false;
    }
    if (supplierJobInput.apn?.trim() === '' && checkedFields.includes('apn')) {
      newErrors.apn = 'APN is required.';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmitJob = async () => {
    validateInputsVerify();
    validateAddressInputs();
    if (!validateAddressInputs()) {
      setShowErrorAbove(true);
      return;
    }
    if (!validateInputsVerify()) {
      setShowErrorAbove(true);
      return;
    }
    setLoading(true);
    let data = {} as PartialSupplierJobUpdatePick;
    checkedFields.forEach((field) => {
      if (field === 'jobNumber') {
        data = {
          ...data,
          jobNumber: supplierJobInput.jobNumber || '',
        };
      }
      if (field === 'owners') {
        data = {
          ...data,
          owners: formAddressState.owners || addressData,
        };
      }
      if (field === 'gcs') {
        data = {
          ...data,
          GCs: formAddressState.GCs || addressData,
        };
      }
      if (field === 'lenders') {
        data = {
          ...data,
          lenders: formAddressState.lenders || [],
        };
      }
      if (field === 'sureties') {
        data = {
          ...data,
          sureties: formAddressState.sureties || [],
        };
      }
      if (field === 'apn') {
        data = {
          ...data,
          apn: supplierJobInput.apn || '',
        };
      }
    });

    const bulkUpdatedSupplierJob = await dispatch(
      bulkUpdateSupplierJobs({
        supplierId: currentSupplier?.id || '',
        supplierJobId: selectedSupplierJobs.map((job) => job.id).join(','),
        supplierJobDetails: { ...data },
      })
    );
    if (bulkUpdatedSupplierJob.type === 'supplierJobs/bulkUpdateSupplierJobs/fulfilled') {
      const success = bulkUpdatedSupplierJob?.payload?.success ?? true;
      if (success) {
        openDillToast({
          message: `Job${selectedSupplierJobs.length > 1 ? 's' : ''} successfully updated!`,
          type: 'SUCCESS',
        });
      } else {
        const updatedJobs: SupplierJobApiResponse[] = bulkUpdatedSupplierJob?.payload?.data ?? [];
        const failedJobs = selectedSupplierJobs.filter(
          (job) => !updatedJobs.map((updated) => updated.id).includes(job.id)
        );
        openDillToast({
          message: `Failed to update ${
            selectedSupplierJobs.length > 1 ? ' some jobs' : 'this job'
          }: ${failedJobs.map((job) => job.name).join(', ')}`,
          type: 'ERROR',
        });
        setLoading(false);
      }
      handleCloseModal();
    } else {
      openDillToast({
        message: `Failed to update job${selectedSupplierJobs.length > 1 ? 's' : ''}`,
        type: 'ERROR',
      });
      setLoading(false);
    }
  };

  const onTextChange = (key: string, value: string) => {
    setShowErrorAbove(false);
    setSupplierJobInput((prev) => {
      return {
        ...prev,
        [key]: value,
      };
    });
  };

  const allFields = [
    { value: 'jobNumber', label: 'Job Number' },
    { value: 'owners', label: 'Owners' },
    { value: 'gcs', label: 'GCs' },
    { value: 'apn', label: 'APN' },
    { value: 'lenders', label: 'Lenders' },
    { value: 'sureties', label: 'Sureties' },
  ];

  const handleUpdateSupplierJobAddress = ({
    field,
    value,
    index,
    key,
  }: {
    field: keyof SupplierJobAddressDetails;
    value: string;
    index: number;
    key: 'GCs' | 'owners' | 'lenders' | 'sureties';
  }) => {
    setAddressFormState((prevState) => {
      const updated = [...(prevState[key] ?? [])];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prevState, [key]: updated };
    });
    setAddressError({
      GCs: '',
      owners: '',
      lenders: '',
      sureties: '',
    });
  };

  const handleAddAddress = (key: 'GCs' | 'owners' | 'lenders' | 'sureties') => {
    let moreFields = {};
    if (key === 'sureties') {
      moreFields = {
        bondNumber: '',
      };
    }
    setAddressFormState((prev) => ({
      ...prev,
      [key]: [
        ...(prev[key] ?? []),
        {
          name: '',
          address: '',
          city: '',
          state: '',
          zipCode: '',
          ...moreFields,
        },
      ],
    }));
    setAddressError({
      GCs: '',
      owners: '',
      lenders: '',
      sureties: '',
    });
  };

  const handleRemoveAddress = (index: number, key: 'GCs' | 'owners' | 'lenders' | 'sureties') => {
    setAddressFormState((prev) => {
      const previousState = [...(prev[key] ?? [])];
      const updated = previousState.filter(
        (_: SupplierJobAddressDetails, i: number) => i !== index
      );
      return { ...prev, [key]: updated };
    });
    setAddressError({
      GCs: '',
      owners: '',
      lenders: '',
      sureties: '',
    });
  };

  const handleOwnerAndGCSame = (value: boolean) => {
    if (value) {
      const data = {
        owners:
          formAddressState.owners &&
          formAddressState.owners?.length > 0 &&
          formAddressState.owners[0].name !== ''
            ? formAddressState.owners
            : formAddressState.GCs,
        GCs:
          formAddressState.GCs &&
          formAddressState.GCs?.length > 0 &&
          formAddressState.GCs[0].name !== ''
            ? formAddressState.GCs
            : formAddressState.owners,
      };
      setAddressFormState((prevState) => ({
        ...prevState,
        ...data,
      }));
    }
    setOwnerAndGCSame(value);
  };

  return (
    <Modal
      open={open}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description">
      <>
        <div className={`new-job-modal-content pt-3`}>
          <div className="new-job-modal-title-container">
            <h3 className="font-medium text-lg">{'Bulk Edit Jobs'}</h3>
            <AppButton
              type="FLAT"
              buttonStyles={{ padding: '0px', margin: '5px 2px' }}
              icon={<X color={COLORS.GREY_300} size={30} />}
              onClick={handleClose}
            />
          </div>
          <div className="flex flex-col w-full px-6 py-3">
            <div className="text-sm font-semibold mb-2">Select Fields to Edit</div>
            <div className="flex flex-wrap w-full">
              {allFields.map((field) => (
                <div className="flex items-center mb-2 w-1/3" key={field.value}>
                  <AppCheckBox
                    isChecked={checkedFields.includes(field.value)}
                    onClick={() => toggleSelectField(field.value)}
                  />
                  <div className="text-sm ml-2">{field.label}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="flex flex-col w-full px-6 py-3 max-h-[60vh] !overflow-y-auto">
            {checkedFields.includes('jobNumber') && (
              <div className="flex flex-col w-full mb-2">
                <div className="w-full">
                  <AppInputField
                    id="jobNumber"
                    label="Job Number"
                    placeholder="Job Number"
                    isRequired={true}
                    value={supplierJobInput.jobNumber}
                    errorText={errors.jobNumber ? errors.jobNumber : ''}
                    onTextChange={(text) => {
                      onTextChange('jobNumber', text);
                    }}
                  />
                </div>
              </div>
            )}
            {checkedFields.includes('apn') && (
              <div className="flex flex-col w-full mt-2">
                <div className="w-full">
                  <AppInputField
                    id="apn"
                    label="APN"
                    placeholder="APN"
                    value={supplierJobInput.apn}
                    isRequired={true}
                    errorText={errors.apn ? errors.apn : ''}
                    onTextChange={(text) => {
                      onTextChange('apn', text);
                    }}
                  />
                </div>
              </div>
            )}
            {checkedFields.includes('gcs') && (
              <div className="bg-slate-100 px-2 py-2 rounded-lg my-4">
                <div className="text-sm font-semibold">GC</div>
                {formAddressState?.GCs?.map((entry, index) => {
                  return (
                    <div
                      key={`bulk-edit-gc-address-input-${index}`}
                      className="border-b GREY_300-BORDER pb-2 mb-1">
                      {formAddressState?.GCs?.length > 1 && (
                        <div className="w-full flex justify-end">
                          <button
                            type="button"
                            className={'text-red-500 text-sm self-start'}
                            onClick={() => handleRemoveAddress(index, 'GCs')}>
                            Remove
                          </button>
                        </div>
                      )}
                      <div className="flex flex-row  pb-3">
                        <div className="w-full relative">
                          <AppInputField
                            id={`gc-${index}`}
                            label="GC Name"
                            inputType="text"
                            placeholder="GC Name"
                            isRequired={true}
                            value={entry.name}
                            errorText={
                              formAddressError.GCs !== '' && entry.name === ''
                                ? 'GC Name is required'
                                : ''
                            }
                            onTextChange={(text) => {
                              handleUpdateSupplierJobAddress({
                                value: text,
                                key: 'GCs',
                                field: 'name',
                                index: index,
                              });
                            }}
                          />
                        </div>
                      </div>
                      <div className="flex flex-row  pb-3">
                        <div className="w-full ">
                          <AppInputField
                            id={`gc-address-${index}`}
                            label="GC Address"
                            inputType="text"
                            placeholder="GC Address"
                            isRequired={true}
                            value={entry.address}
                            errorText={
                              formAddressError.GCs !== '' && entry.address === ''
                                ? 'GC Address is required'
                                : ''
                            }
                            onTextChange={(text) => {
                              handleUpdateSupplierJobAddress({
                                value: text,
                                key: 'GCs',
                                field: 'address',
                                index: index,
                              });
                            }}
                          />
                        </div>
                      </div>
                      <div className="flex flex-row w-full gap-1">
                        <AppInputField
                          id={`gc-city-${index}`}
                          label="GC City"
                          inputType="text"
                          placeholder="GC City"
                          isRequired={true}
                          value={entry.city}
                          errorText={
                            formAddressError.GCs !== '' && entry.city === ''
                              ? 'GC City is required'
                              : ''
                          }
                          onTextChange={(text) => {
                            handleUpdateSupplierJobAddress({
                              value: text,
                              key: 'GCs',
                              field: 'city',
                              index: index,
                            });
                          }}
                        />
                        <div className="w-full">
                          <AppDropDown2
                            isRequired
                            label="GC State"
                            value={entry.state || null}
                            errorText={
                              formAddressError.GCs !== '' && entry.state === ''
                                ? 'GC State is required'
                                : ''
                            }
                            items={[
                              ...Object.keys(allStates).map((stateName) => {
                                const key = stateName as keyof typeof allStates;
                                const stateKey = allStates[key];
                                return {
                                  label: `${stateKey} - ${stateName}`,
                                  value: stateKey,
                                };
                              }),
                            ]}
                            onSelectChange={(item) => {
                              if (item && item?.value !== null) {
                                const foundState = Object.values(getAllStates()).find(
                                  (stateName) => stateName === item?.value
                                );
                                if (foundState) {
                                  handleUpdateSupplierJobAddress({
                                    value: item.value,
                                    key: 'GCs',
                                    field: 'state',
                                    index: index,
                                  });
                                }
                              }
                            }}
                          />
                        </div>
                        <AppInputField
                          id={`gc-zip-${index}`}
                          label="GC Zip Code"
                          inputType="text"
                          placeholder="12345"
                          isRequired={true}
                          value={entry.zipCode}
                          errorText={
                            formAddressError.GCs !== '' && entry.zipCode === ''
                              ? 'GC Zip Code is required'
                              : ''
                          }
                          onTextChange={(text) => {
                            handleUpdateSupplierJobAddress({
                              value: text,
                              key: 'GCs',
                              field: 'zipCode',
                              index: index,
                            });
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
                <div className="w-full flex mb-2 justify-end">
                  <AppButton
                    text="Add GC"
                    onClick={() => handleAddAddress('GCs')}
                    icon={<PlusCircle color={COLORS.WHITE} size={12} />}
                    buttonStyles={{
                      height: '30px',
                      borderRadius: '5px',
                    }}
                  />
                </div>
              </div>
            )}
            {checkedFields.includes('gcs') && checkedFields.includes('owners') && (
              <div className="flex w-full py-1">
                <AppCheckBox
                  className="mr-2"
                  isChecked={ownerAndGCSame}
                  onClick={handleOwnerAndGCSame}
                />
                <div className="text-black text-sm">Owner and GC are the same.</div>
              </div>
            )}
            {checkedFields.includes('owners') && (
              <div className="bg-slate-100 px-2 py-2 rounded-lg my-4">
                <div className="text-sm font-semibold">Owner</div>
                {formAddressState.owners.map((entry, index) => {
                  return (
                    <div
                      key={`owner-bulk-edit-address-input-${index}`}
                      className="border-b GREY_300-BORDER pb-2 mb-1">
                      {formAddressState.owners.length > 1 && (
                        <div className="w-full flex justify-end">
                          <button
                            type="button"
                            className={'text-sm self-start text-red-500 text-sm self-start'}
                            onClick={() => handleRemoveAddress(index, 'owners')}>
                            Remove
                          </button>
                        </div>
                      )}
                      <div className="flex flex-row  pb-3">
                        <div className="w-full relative">
                          <AppInputField
                            id={`owner-${index}`}
                            label="Owner Name"
                            inputType="text"
                            placeholder="Owner Name"
                            isRequired={true}
                            value={entry.name}
                            errorText={
                              formAddressError.owners !== '' && entry.name === ''
                                ? 'Owner Name is required'
                                : ''
                            }
                            onTextChange={(text) => {
                              handleUpdateSupplierJobAddress({
                                value: text,
                                key: 'owners',
                                field: 'name',
                                index: index,
                              });
                            }}
                          />
                        </div>
                      </div>
                      <div className="flex flex-row  pb-3">
                        <div className="w-full ">
                          <AppInputField
                            id={`owner-address-${index}`}
                            label="Owner Address"
                            inputType="text"
                            placeholder="Owner Address"
                            isRequired={true}
                            value={entry.address}
                            errorText={
                              formAddressError.owners !== '' && entry.address === ''
                                ? 'Owner Address is required'
                                : ''
                            }
                            onTextChange={(text) => {
                              handleUpdateSupplierJobAddress({
                                value: text,
                                key: 'owners',
                                field: 'address',
                                index: index,
                              });
                            }}
                          />
                        </div>
                      </div>
                      <div className="flex flex-row w-full gap-1">
                        <AppInputField
                          id={`owner-city-${index}`}
                          label="Owner City"
                          inputType="text"
                          placeholder="Owner City"
                          isRequired={true}
                          value={entry.city}
                          errorText={
                            formAddressError.owners !== '' && entry.city === ''
                              ? 'Owner City is required'
                              : ''
                          }
                          onTextChange={(text) => {
                            handleUpdateSupplierJobAddress({
                              value: text,
                              key: 'owners',
                              field: 'city',
                              index: index,
                            });
                          }}
                        />
                        <div className="w-full">
                          <AppDropDown2
                            isRequired
                            label="Owner State"
                            value={entry.state || null}
                            errorText={
                              formAddressError.owners !== '' && entry.state === ''
                                ? 'Owner State is required'
                                : ''
                            }
                            items={[
                              ...Object.keys(allStates).map((stateName) => {
                                const key = stateName as keyof typeof allStates;
                                const stateKey = allStates[key];
                                return {
                                  label: `${stateKey} - ${stateName}`,
                                  value: stateKey,
                                };
                              }),
                            ]}
                            onSelectChange={(item) => {
                              if (item && item?.value !== null) {
                                const foundState = Object.values(getAllStates()).find(
                                  (stateName) => stateName === item?.value
                                );
                                if (foundState) {
                                  handleUpdateSupplierJobAddress({
                                    value: item.value,
                                    key: 'owners',
                                    field: 'state',
                                    index: index,
                                  });
                                }
                              }
                            }}
                          />
                        </div>
                        <AppInputField
                          id={`owner-zip-${index}`}
                          label="Owner Zip Code"
                          inputType="text"
                          placeholder="12345"
                          isRequired={true}
                          value={entry.zipCode}
                          errorText={
                            formAddressError.owners !== '' && entry.zipCode === ''
                              ? 'Owner Zip Code is required'
                              : ''
                          }
                          onTextChange={(text) => {
                            handleUpdateSupplierJobAddress({
                              value: text,
                              key: 'owners',
                              field: 'zipCode',
                              index: index,
                            });
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
                <div className="w-full flex mb-2 justify-end">
                  <AppButton
                    text="Add Owner"
                    onClick={() => handleAddAddress('owners')}
                    icon={<PlusCircle color={COLORS.WHITE} size={12} />}
                    buttonStyles={{
                      height: '30px',
                      borderRadius: '5px',
                    }}
                  />
                </div>
              </div>
            )}
            {checkedFields.includes('lenders') && (
              <div>
                <div className="bg-slate-100 px-2 py-2 rounded-lg my-4">
                  <div className="text-sm font-semibold">Lender</div>
                  {formAddressState.lenders &&
                    formAddressState.lenders.map((entry, index) => (
                      <div
                        key={`lender-address-input-${index}`}
                        className="border-b GREY_300-BORDER pb-2 mb-1">
                        {formAddressState.lenders && formAddressState.lenders.length > 1 && (
                          <div className="w-full flex justify-end">
                            <button
                              type="button"
                              className={'text-red-500 text-sm self-start'}
                              onClick={() => handleRemoveAddress(index, 'lenders')}>
                              Remove
                            </button>
                          </div>
                        )}
                        <div className="flex flex-row  pb-3">
                          <div className="w-full">
                            <AppInputField
                              id={`lender-${index}`}
                              label="Lender Name"
                              inputType="text"
                              placeholder="Lender Name"
                              isRequired={true}
                              value={entry.name}
                              errorText={
                                formAddressError.lenders !== '' && entry.name === ''
                                  ? 'Lender Name is required'
                                  : ''
                              }
                              onTextChange={(text) => {
                                handleUpdateSupplierJobAddress({
                                  value: text,
                                  key: 'lenders',
                                  field: 'name',
                                  index: index,
                                });
                              }}
                            />
                          </div>
                        </div>
                        <div className="flex flex-row  pb-3">
                          <div className="w-full ">
                            <AppInputField
                              id={`lender-address-${index}`}
                              label="Lender Address"
                              inputType="text"
                              placeholder="Lender Address"
                              isRequired={true}
                              value={entry.address}
                              errorText={
                                formAddressError.lenders !== '' && entry.address === ''
                                  ? 'Lender Address is required'
                                  : ''
                              }
                              onTextChange={(text) => {
                                handleUpdateSupplierJobAddress({
                                  value: text,
                                  key: 'lenders',
                                  field: 'address',
                                  index: index,
                                });
                              }}
                            />
                          </div>
                        </div>
                        <div className="flex flex-row  pb-3 gap-1">
                          <AppInputField
                            id={`lender-city-${index}`}
                            label="Lender City"
                            inputType="text"
                            placeholder="Lender City"
                            isRequired={true}
                            value={entry.city}
                            errorText={
                              formAddressError.lenders !== '' && entry.city === ''
                                ? 'Lender City is required'
                                : ''
                            }
                            onTextChange={(text) => {
                              handleUpdateSupplierJobAddress({
                                value: text,
                                key: 'lenders',
                                field: 'city',
                                index: index,
                              });
                            }}
                          />
                          <AppDropDown2
                            isRequired
                            label="Lender State"
                            value={entry.state || null}
                            errorText={
                              formAddressError.lenders !== '' && entry.state === ''
                                ? 'Lender State is required'
                                : ''
                            }
                            items={[
                              ...Object.keys(allStates).map((stateName) => {
                                const key = stateName as keyof typeof allStates;
                                const stateKey = allStates[key];
                                return {
                                  label: `${stateKey} - ${stateName}`,
                                  value: stateKey,
                                };
                              }),
                            ]}
                            onSelectChange={(item) => {
                              if (item && item?.value !== null) {
                                const foundState = Object.values(getAllStates()).find(
                                  (stateName) => stateName === item?.value
                                );
                                if (foundState) {
                                  handleUpdateSupplierJobAddress({
                                    value: item.value,
                                    key: 'lenders',
                                    field: 'state',
                                    index: index,
                                  });
                                }
                              }
                            }}
                          />
                          <AppInputField
                            id={`lender-zip-${index}`}
                            label="Lender Zip"
                            inputType="text"
                            placeholder="Lender Zip"
                            isRequired={true}
                            value={entry.zipCode}
                            errorText={
                              formAddressError.lenders !== '' && entry.zipCode === ''
                                ? 'Lender Zip Code is required'
                                : ''
                            }
                            onTextChange={(text) => {
                              handleUpdateSupplierJobAddress({
                                value: text,
                                key: 'lenders',
                                field: 'zipCode',
                                index: index,
                              });
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  <div className="w-full flex mb-2 justify-end">
                    <AppButton
                      text="Add Lender"
                      onClick={() => handleAddAddress('lenders')}
                      icon={<PlusCircle color={COLORS.WHITE} size={12} />}
                      buttonStyles={{
                        height: '30px',
                        borderRadius: '5px',
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
            {checkedFields.includes('sureties') && (
              <div>
                <div className="bg-slate-100 px-2 py-2 rounded-lg my-4">
                  <div className="text-sm font-semibold">Surety</div>
                  {formAddressState.sureties &&
                    formAddressState.sureties.map((entry, index) => (
                      <div
                        key={`sureties-address-${index}`}
                        className="border-b GREY_300-BORDER pb-2 mb-1">
                        {formAddressState.sureties && formAddressState.sureties?.length > 1 && (
                          <div className="w-full flex justify-end">
                            <button
                              type="button"
                              className={'text-red-500 text-sm self-start'}
                              onClick={() => handleRemoveAddress(index, 'sureties')}>
                              Remove
                            </button>
                          </div>
                        )}
                        <div className="flex flex-row  pb-3">
                          <div className="w-full">
                            <AppInputField
                              id={`surety-${index}`}
                              label="Surety"
                              inputType="text"
                              placeholder="Surety"
                              isRequired={true}
                              value={entry.name}
                              errorText={
                                formAddressError.sureties !== '' && entry.name === ''
                                  ? 'Surety Name is required'
                                  : ''
                              }
                              onTextChange={(text) => {
                                handleUpdateSupplierJobAddress({
                                  value: text,
                                  key: 'sureties',
                                  field: 'name',
                                  index: index,
                                });
                              }}
                            />
                          </div>
                        </div>
                        <div className="flex flex-row  pb-3">
                          <div className="w-full">
                            <AppInputField
                              id={`suretyAddress-${index}`}
                              label="Surety Address"
                              inputType="text"
                              placeholder="Surety Address"
                              isRequired={true}
                              value={entry.address}
                              errorText={
                                formAddressError.sureties !== '' && entry.address === ''
                                  ? 'Surety Address is required'
                                  : ''
                              }
                              onTextChange={(text) => {
                                handleUpdateSupplierJobAddress({
                                  value: text,
                                  key: 'sureties',
                                  field: 'address',
                                  index: index,
                                });
                              }}
                            />
                          </div>
                        </div>
                        <div className="flex flex-row  pb-3 gap-2">
                          <div className="w-full">
                            <AppInputField
                              id={`suretyCity-${index}`}
                              label="Surety City"
                              inputType="text"
                              placeholder="Surety City"
                              isRequired={true}
                              value={entry.city}
                              errorText={
                                formAddressError.sureties !== '' && entry.city === ''
                                  ? 'Surety City is required'
                                  : ''
                              }
                              onTextChange={(text) => {
                                handleUpdateSupplierJobAddress({
                                  value: text,
                                  key: 'sureties',
                                  field: 'city',
                                  index: index,
                                });
                              }}
                            />
                          </div>
                          <div className="w-full">
                            <AppDropDown2
                              label="Surety State"
                              isRequired={true}
                              value={entry.state || null}
                              errorText={
                                formAddressError.sureties !== '' && entry.state === ''
                                  ? 'Surety State is required'
                                  : ''
                              }
                              items={[
                                ...Object.keys(allStates).map((stateName) => {
                                  const key = stateName as keyof typeof allStates;
                                  const stateKey = allStates[key];
                                  return {
                                    label: `${stateKey} - ${stateName}`,
                                    value: stateKey,
                                  };
                                }),
                              ]}
                              onSelectChange={(item) => {
                                if (item && item?.value !== null) {
                                  const foundState = Object.values(getAllStates()).find(
                                    (stateName) => stateName === item?.value
                                  );
                                  if (foundState) {
                                    handleUpdateSupplierJobAddress({
                                      value: item.value,
                                      key: 'sureties',
                                      field: 'state',
                                      index: index,
                                    });
                                  }
                                }
                              }}
                            />
                          </div>
                          <div className="w-full">
                            <AppInputField
                              id={`suretyZip-${index}`}
                              label="Surety Zip"
                              inputType="text"
                              placeholder="Surety Zip"
                              isRequired={true}
                              value={entry.zipCode}
                              errorText={
                                formAddressError.sureties !== '' && entry.zipCode === ''
                                  ? 'Surety Zip Code is required'
                                  : ''
                              }
                              onTextChange={(text) => {
                                handleUpdateSupplierJobAddress({
                                  value: text,
                                  key: 'sureties',
                                  field: 'zipCode',
                                  index: index,
                                });
                              }}
                            />
                          </div>
                        </div>
                        <div className="flex flex-row  pb-3">
                          <div className="w-full">
                            <AppInputField
                              id={`bondNumber-${index}`}
                              label="Bond Number"
                              inputType="text"
                              placeholder="Bond Number"
                              isRequired={true}
                              value={entry.bondNumber}
                              errorText={
                                formAddressError.sureties !== '' && entry.bondNumber === ''
                                  ? 'Bond Number is required'
                                  : ''
                              }
                              onTextChange={(text) => {
                                handleUpdateSupplierJobAddress({
                                  value: text,
                                  key: 'sureties',
                                  field: 'bondNumber',
                                  index: index,
                                });
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  <div className="w-full flex mb-2 justify-end">
                    <AppButton
                      text="Add Surety"
                      onClick={() => handleAddAddress('sureties')}
                      icon={<PlusCircle color={COLORS.WHITE} size={12} />}
                      buttonStyles={{
                        height: '30px',
                        borderRadius: '5px',
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="flex flex-col w-full mb-3">
            <hr className="h-px my-2 bg-gray-200 border-0 dark:bg-gray-200"></hr>
            <div className="flex flex-row self-end px-6">
              <AppButton
                text="Close"
                type="TERTIARY"
                buttonStyles={{
                  marginTop: '4px',
                  marginBottom: '4px',
                  marginRight: '12px',
                  height: '40px',
                }}
                onClick={handleClose}
              />
              <AppButton
                text={'Bulk Edit Jobs'}
                onClick={() => handleSubmitJob()}
                buttonStyles={{
                  marginTop: '4px',
                  marginBottom: '4px',
                  height: '40px',
                  width: '120px',
                }}
                isDisabled={loading || checkedFields.length === 0}
                isLoading={loading}
              />
            </div>
          </div>
        </div>
      </>
    </Modal>
  );
};
